import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Shield,
  Award,
  Users,
  CheckCircle,
  AlertTriangle,
  Droplet,
  Heart,
  ArrowRight,
  FileText,
} from 'lucide-react';

const ICON_MAP = {
  shield: Shield,
  award: Award,
  users: Users,
  check: CheckCircle,
  alert: AlertTriangle,
  droplet: Droplet,
  heart: Heart,
  file: FileText,
};

const SafetySection = ({ config }) => {
  if (!config) return null;

  const {
    hero_title = 'Safety & Standards',
    hero_subtitle = "Your child's safety is our highest priority.",
    trust_badges = [],
    standards_cards = [],
  } = config;

  return (
    <section className="py-20 bg-slate-900 text-white" data-testid="safety-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Safety First</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {hero_title}
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            {hero_subtitle}
          </p>
        </div>

        {/* Trust Badges Strip */}
        {trust_badges.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {trust_badges.map((badge, idx) => {
              const IconComponent = ICON_MAP[badge.icon] || Shield;
              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10"
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm font-medium text-white">{badge.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Standards Cards Preview (2 cards) */}
        {standards_cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {standards_cards.slice(0, 2).map((card) => {
              const IconComponent = ICON_MAP[card.icon] || Shield;
              return (
                <Card key={card.id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {card.bullets?.slice(0, 3).map((bullet, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <Link to="/safety">
            <Button
              size="lg"
              className="bg-white text-slate-900 hover:bg-slate-100 font-semibold px-8 py-6 rounded-xl group"
            >
              View Full Safety Standards
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SafetySection;
